import { useEffect, useRef } from 'react'

type ConsentCategories = {
  analytics: boolean
  advertisement: boolean
  functional: boolean
}

function getConsentFromEvent(detail: any): ConsentCategories {
  if (detail?.categories) {
    return {
      analytics: !!detail.categories.analytics,
      advertisement: !!detail.categories.advertisement,
      functional: !!detail.categories.functional,
    }
  }
  if (detail?.accepted) {
    return {
      analytics: detail.accepted.includes('analytics'),
      advertisement: detail.accepted.includes('advertisement'),
      functional: detail.accepted.includes('functional'),
    }
  }
  return { analytics: false, advertisement: false, functional: false }
}

function injectScript(src: string, id: string): void {
  if (document.getElementById(id)) return
  const script = document.createElement('script')
  script.id = id
  script.type = 'text/javascript'
  script.async = true
  script.src = src
  document.head.appendChild(script)
}

function injectInlineScript(code: string, id: string): void {
  if (document.getElementById(id)) return
  const script = document.createElement('script')
  script.id = id
  script.type = 'text/javascript'
  script.textContent = code
  document.head.appendChild(script)
}

function loadClarity(): void {
  const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID
  if (!projectId) return
  injectInlineScript(
    `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${projectId}");`,
    'consent-clarity'
  )
}

function loadHeap(): void {
  const appId = process.env.NEXT_PUBLIC_HEAP_APP_ID
  if (!appId) return
  injectInlineScript(
    `window.heapReadyCb=window.heapReadyCb||[],window.heap=window.heap||[],heap.load=function(e,t){window.heap.envId=e,window.heap.clientConfig=t=t||{},window.heap.clientConfig.shouldFetchServerConfig=!1;var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="https://cdn.us.heap-api.com/config/"+e+"/heap_config.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(a,r);var n=["init","startTracking","stopTracking","track","resetIdentity","identify","getSessionId","getUserId","getIdentity","addUserProperties","addEventProperties","removeEventProperty","clearEventProperties","addAccountProperties","addAdapter","addTransformer","addTransformerFn","onReady","addPageviewProperties","removePageviewProperty","clearPageviewProperties","trackPageview"],i=function(e){return function(){var t=Array.prototype.slice.call(arguments,0);window.heapReadyCb.push({name:e,fn:function(){heap[e]&&heap[e].apply(heap,t)}})}};for(var p=0;p<n.length;p++)heap[n[p]]=i(n[p])};heap.load("${appId}");`,
    'consent-heap'
  )
}

function loadMixpanel(): void {
  const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN
  if (!token) return
  injectInlineScript(
    `(function(f,b){if(!b.__SV){var e,g,i,h;window.mixpanel=b;b._i=[];b.init=function(e,f,c){function g(a,d){var b=d.split(".");2==b.length&&((a=a[b[0]]),(d=b[1]));a[d]=function(){a.push([d].concat(Array.prototype.slice.call(arguments,0)));};}var a=b;"undefined"!==typeof c?(a=b[c]=[]):(c="mixpanel");a.people=a.people||[];a.toString=function(a){var d="mixpanel";"mixpanel"!==c&&(d+="."+c);a||(d+=" (stub)");return d;};a.people.toString=function(){return a.toString(1)+".people (stub)";};i="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");for(h=0;h<i.length;h++)g(a,i[h]);var j="set set_once union unset remove delete".split(" ");a.get_group=function(){function b(c){d[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));a.push([e,call2]);};}for(var d={},e=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<j.length;c++)b(j[c]);return d;};b._i.push([e,f,c]);};b.__SV=1.2;e=f.createElement("script");e.type="text/javascript";e.async=!0;e.src="//cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";g=f.getElementsByTagName("script")[0];g.parentNode.insertBefore(e,g);}})(document,window.mixpanel||[]);mixpanel.init("${token}",{track_pageview:true,persistence:"localStorage"});`,
    'consent-mixpanel'
  )
}

function loadHubSpot(): void {
  const portalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID
  if (!portalId) return
  injectScript(
    `//js.hs-scripts.com/${portalId}.js`,
    'consent-hubspot'
  )
}

function loadGoogleTagManager(): void {
  const containerId = process.env.NEXT_PUBLIC_GTM_CONTAINER_ID
  if (!containerId) return
  injectInlineScript(
    `window.dataLayer=window.dataLayer||[];window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});`,
    'consent-gtm-datalayer'
  )
  injectScript(
    `https://www.googletagmanager.com/gtm.js?id=${containerId}`,
    'consent-gtm'
  )
}

const ConsentGatedAnalytics = () => {
  const analyticsLoaded = useRef(false)
  const marketingLoaded = useRef(false)

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ANALYTICS_DISABLED === 'true') return

    const loadAnalytics = () => {
      if (analyticsLoaded.current) return
      analyticsLoaded.current = true
      loadClarity()
      loadHeap()
      loadMixpanel()
    }

    const loadMarketing = () => {
      if (marketingLoaded.current) return
      marketingLoaded.current = true
      loadHubSpot()
      loadGoogleTagManager()
    }

    const handleConsent = (event: Event) => {
      const consent = getConsentFromEvent((event as CustomEvent).detail)
      if (consent.analytics) loadAnalytics()
      if (consent.advertisement) loadMarketing()
    }

    document.addEventListener('cookieyes_banner_load', handleConsent)
    document.addEventListener('cookieyes_consent_update', handleConsent)

    return () => {
      document.removeEventListener('cookieyes_banner_load', handleConsent)
      document.removeEventListener('cookieyes_consent_update', handleConsent)
    }
  }, [])

  return null
}

export default ConsentGatedAnalytics
